import React, { useState } from 'react';
import './AuditModal.css';

const AuditModal = ({ log, onClose }) => {
  const [showUnchanged, setShowUnchanged] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatJson = (jsonString) => {
    try {
      if (!jsonString) return 'No disponible';
      const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      return jsonString || 'No disponible';
    }
  };

  const getEntityTypeLabel = (type) => {
    const labels = {
      'USER': 'Usuario',
      'PIZZERIA': 'Pizzería',
      'BRANCH': 'Sucursal',
      'PROMOTION': 'Promoción'
    };
    return labels[type] || type;
  };

  const getActionLabel = (action) => {
    const labels = {
      'CREATE': 'Crear',
      'UPDATE': 'Actualizar',
      'DELETE': 'Eliminar'
    };
    return labels[action] || action;
  };

  const getActionColor = (action) => {
    const colors = {
      'CREATE': 'success',
      'UPDATE': 'primary',
      'DELETE': 'danger'
    };
    return colors[action] || 'secondary';
  };

  // Función para resaltar diferencias
  const renderDiffView = (oldValues, newValues) => {
    if (!oldValues || !newValues) return null;

    const allKeys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);
    const changes = [];

    // Campos sensibles que no deberían mostrarse en detalle
    const sensitiveFields = ['password', 'confirmation_token', 'recover_token'];
    
    // Función para comparar valores considerando tipos
    const isEqual = (oldVal, newVal) => {
      // Si ambos son undefined o null, son iguales
      if ((oldVal === undefined || oldVal === null) && (newVal === undefined || newVal === null)) return true;
      
      // Si uno es undefined/null y el otro no, son diferentes
      if ((oldVal === undefined || oldVal === null) !== (newVal === undefined || newVal === null)) return false;
      
      // Para coordenadas lat/lng, convertir a números y comparar
      if (typeof oldVal === 'string' && typeof newVal === 'number' && 
          (oldVal.includes('.') || newVal.toString().includes('.'))) {
        const oldNum = parseFloat(oldVal);
        const newNum = parseFloat(newVal);
        if (!isNaN(oldNum) && !isNaN(newNum)) {
          // Comparar con tolerancia para errores de punto flotante
          return Math.abs(oldNum - newVal) < 0.000001;
        }
      }
      
      // Para números vs strings que representan el mismo número
      if (typeof oldVal === 'string' && typeof newVal === 'number') {
        const oldNum = parseFloat(oldVal);
        if (!isNaN(oldNum) && oldNum === newVal) return true;
      }
      
      if (typeof oldVal === 'number' && typeof newVal === 'string') {
        const newNum = parseFloat(newVal);
        if (!isNaN(newNum) && oldVal === newNum) return true;
      }
      
      // Comparación normal
      return oldVal === newVal;
    };

    // Función para detectar si los valores son equivalentes pero de tipos diferentes
    const isEquivalentButDifferentType = (oldVal, newVal, key) => {
      if (oldVal === undefined || newVal === undefined) return false;
      
      // Para coordenadas lat/lng
      if ((key === 'lat' || key === 'lng') && 
          ((typeof oldVal === 'string' && typeof newVal === 'number') ||
           (typeof oldVal === 'number' && typeof newVal === 'string'))) {
        const oldNum = parseFloat(oldVal);
        const newNum = parseFloat(newVal);
        if (!isNaN(oldNum) && !isNaN(newNum)) {
          return Math.abs(oldNum - newNum) < 0.000001;
        }
      }
      
      // Para otros números vs strings
      if (typeof oldVal === 'string' && typeof newVal === 'number') {
        const oldNum = parseFloat(oldVal);
        if (!isNaN(oldNum) && oldNum === newVal) return true;
      }
      
      if (typeof oldVal === 'number' && typeof newVal === 'string') {
        const newNum = parseFloat(newVal);
        if (!isNaN(newNum) && oldVal === newNum) return true;
      }
      
      return false;
    };

    // Función para normalizar valores para mostrar
    const normalizeValue = (value, key) => {
      if (value === undefined || value === null) return 'No disponible';
      
      // Para coordenadas, mostrar siempre como número con 6 decimales
      if ((key === 'lat' || key === 'lng') && (typeof value === 'string' || typeof value === 'number')) {
        const num = parseFloat(value);
        if (!isNaN(num)) {
          return num.toFixed(6);
        }
      }
      
      // Para campos booleanos, mostrar de manera más legible
      if (typeof value === 'boolean') {
        return value ? 'Sí' : 'No';
      }
      
      // Para strings vacíos, mostrar de manera más clara
      if (typeof value === 'string' && value === '') {
        return '(vacío)';
      }
      
      return String(value);
    };
    
    allKeys.forEach(key => {
      const oldValue = oldValues[key];
      const newValue = newValues[key];
      
      // Debug temporal para logo
      if (key === 'logo') {
        console.log('Logo comparison:', {
          key,
          oldValue,
          newValue,
          oldType: typeof oldValue,
          newType: typeof newValue,
          isEqual: isEqual(oldValue, newValue)
        });
      }
      
      // Verificar si es un campo sensible
      const isSensitive = sensitiveFields.includes(key.toLowerCase());
      
      if (!isEqual(oldValue, newValue)) {
        changes.push({
          key,
          oldValue: isSensitive ? '[HIDDEN]' : (oldValue !== undefined ? oldValue : 'No disponible'),
          newValue: isSensitive ? '[HIDDEN]' : (newValue !== undefined ? newValue : 'No disponible'),
          type: 'changed',
          isSensitive
        });
      } else if (oldValue !== undefined && newValue !== undefined) {
        // Solo mostrar campos sin cambios si no son sensibles y si el toggle está activado
        if (!isSensitive && showUnchanged) {
          changes.push({
            key,
            oldValue,
            newValue,
            type: 'unchanged'
          });
        }
      }
    });

    // Ordenar: cambios primero, luego campos sin cambios
    changes.sort((a, b) => {
      if (a.type === 'changed' && b.type !== 'changed') return -1;
      if (a.type !== 'changed' && b.type === 'changed') return 1;
      return a.key.localeCompare(b.key);
    });

    const changedCount = changes.filter(c => c.type === 'changed').length;
    const unchangedCount = changes.filter(c => c.type === 'unchanged').length;

    return (
      <div className="diff-view">
        <div className="diff-header">
          <div className="diff-stats">
            <span className="diff-label changed">{changedCount} cambios</span>
            {unchangedCount > 0 && (
              <span className="diff-label unchanged">{unchangedCount} sin cambios</span>
            )}
          </div>
          <div className="diff-toggle">
            <label className="form-check-label">
              <input
                type="checkbox"
                className="form-check-input me-2"
                checked={showUnchanged}
                onChange={(e) => setShowUnchanged(e.target.checked)}
              />
              Mostrar campos sin cambios
            </label>
          </div>
        </div>
        <div className="diff-content">
          {changes.map((change, index) => {
            const isEquivalent = isEquivalentButDifferentType(
              change.oldValue, 
              change.newValue, 
              change.key
            );
            
            return (
              <div key={index} className={`diff-item ${change.type}`}>
                <div className="diff-key">
                  {change.key}
                  {change.isSensitive && (
                    <span className="sensitive-badge" title="Campo sensible">🔒</span>
                  )}
                  {isEquivalent && (
                    <span className="equivalent-badge" title="Valores equivalentes pero de tipos diferentes">🔄</span>
                  )}
                </div>
                <div className="diff-values">
                  <div className="diff-old">
                    <span className="diff-label">Antes:</span>
                    <span className="diff-value">{normalizeValue(change.oldValue, change.key)}</span>
                  </div>
                  <div className="diff-new">
                    <span className="diff-label">Después:</span>
                    <span className="diff-value">{normalizeValue(change.newValue, change.key)}</span>
                  </div>
                </div>
                {isEquivalent && (
                  <div className="equivalent-note">
                    <small className="text-info">
                      <i className="fas fa-info-circle me-1"></i>
                      Valores equivalentes (diferencia de tipo de dato)
                    </small>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {changes.length === 0 && (
          <div className="diff-empty">
            <p className="text-muted text-center py-3">No se detectaron cambios en los campos visibles</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="audit-modal-overlay" onClick={onClose}>
      <div className="audit-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="audit-modal-header">
          <h4>Detalles del Cambio</h4>
          <button 
            type="button" 
            className="btn-close" 
            onClick={onClose}
            aria-label="Cerrar"
          ></button>
        </div>

        <div className="audit-modal-body">
          {/* Información del Log */}
          <div className="log-info-section mb-4">
            <h6 className="section-title">Información del Log</h6>
            <div className="row">
              <div className="col-md-6">
                <div className="info-item">
                  <strong>Usuario:</strong> {log.userEmail || 'N/A'}
                </div>
                <div className="info-item">
                  <strong>Fecha:</strong> {formatDate(log.createdAt)}
                </div>
              </div>
              
            </div>
          </div>

          {/* Detalles de la Entidad */}
          <div className="entity-details-section mb-4">
            <h6 className="section-title">Detalles de la Entidad</h6>
            <div className="row">  
              <div className="col-md-4">
                <div className="info-item">
                  <strong>Acción:</strong> 
                  <span className={`badge bg-${getActionColor(log.action)} ms-2`}>
                    {getActionLabel(log.action)}
                  </span>
                </div>
              </div>
              <div className="col-md-4">
                <div className="info-item">
                  <strong>Pizzeria:</strong> {log.newValues.fantasy_name || 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Cambios Realizados */}
          <div className="changes-section">
            <h6 className="section-title">Cambios Realizados</h6>
            
            {log.action === 'CREATE' && (
              <div className="change-item">
                <div className="change-label">Nuevos Valores:</div>
                <pre className="change-content">{formatJson(log.newValues)}</pre>
              </div>
            )}

            {log.action === 'UPDATE' && (
              <>
                {/* Vista de diferencias resaltadas */}
                {/*
                <div className="change-item mb-3">
                  <div className="change-label">Resumen de Cambios:</div>
                  {renderDiffView(log.oldValues, log.newValues)}
                </div>
                */} 
                {/* Vista JSON completa */}
                <div className="change-item mb-3">
                  <div className="change-label">Valores Completos (JSON):</div>
                  <div className="json-comparison">
                    <div className="json-old">
                      <div className="json-header">Valores Anteriores:</div>
                      <pre className="change-content before-values">{formatJson(log.oldValues)}</pre>
                    </div>
                    <div className="json-new">
                      <div className="json-header">Valores Nuevos:</div>
                      <pre className="change-content after-values">{formatJson(log.newValues)}</pre>
                    </div>
                  </div>
                </div>
              </>
            )}

            {log.action === 'DELETE' && (
              <div className="change-item">
                <div className="change-label">Valores Eliminados:</div>
                <pre className="change-content before-values">{formatJson(log.oldValues)}</pre>
              </div>
            )}

            {!log.oldValues && !log.newValues && (
              <div className="no-changes-info">
                <p className="text-muted text-center">
                  <i className="fas fa-info-circle me-2"></i>
                  No hay cambios detallados disponibles para esta acción
                </p>
              </div>
            )}

            {log.description && (
              <div className="description-section mt-3">
                <strong>Descripción:</strong>
                <p className="description-text">{log.description}</p>
              </div>
            )}
          </div>
        </div>

        <div className="audit-modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditModal;
